/**
 * Histopathology Outsource Test – migrated from admin4.0 HistopathOutsource.js
 * Same design, layout, colors, font sizes; functional component; all flows preserved.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Stack,
  Tooltip,
  Typography,
  Drawer,
  Autocomplete,
  TextField,
  IconButton,
  Modal,
  Paper,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import CloseIcon from "@mui/icons-material/Close";
import ImagePaths from "../../Utility/ImagePaths";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import { Colors } from "../../Styles/Colors";
import {
  CommonGridToolBarWithFilterText,
  ConfirmPopup,
  AmountsCard,
} from "../../Components/Common Components/CommonComponents";
import OpenWebCamPopUP from "../../Components/Common Components/OpenWebCamPopUP";
import { CheckAccessFunc } from "../../Components/Common Components/DashboardComponents";
import CommonDatePicker from "../../Components/Common Components/CommonDatePicker";
import CommonValidation from "../../Utility/CommonValidation";
import "../../Styles/Configuration.css";

function formatDate(isoString) {
  const raw = isoString != null ? String(isoString).trim() : "";
  if (!raw) return "-";
  const d = DateTime.fromISO(raw);
  return d.isValid ? d.toFormat("dd-MM-yyyy") : "-";
}

function formatDateWithTime(isoString) {
  if (!isoString) return "- | -";
  const dateObj = new Date(isoString);
  const day = dateObj.getDate().toString().padStart(2, "0");
  const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
  const year = dateObj.getFullYear();
  const date = `${day}-${month}-${year}`;
  let hours = dateObj.getHours();
  const minutes = dateObj.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${date} | ${hours}:${minutes} ${ampm}`;
}

function dataURItoFile(dataURI, filename) {
  const arr = dataURI.split(",");
  const mime = arr[0].match(/:(.*?);/)?.[1] || "image/jpeg";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
}

function OutsourcePatientDetails({ data }) {
  const patient_name = data?.patient_name || "-";
  const patient_age = data?.patient_age?.toString()?.includes("Y") ? data?.patient_age : data?.age?.toString()?.includes("Y") ? data?.age : data?.patient_age_str || (data?.age ? `${data?.age}Y` : "-");
  const patient_gender = data?.patient_gender || data?.gender || "";
  const whatGender = /male|^m$/i.test(patient_gender) ? "M" : /female|^f$/i.test(patient_gender) ? "F" : "-";
  const PatientAccNumber = data?.nxt_id ?? "-";
  const mobile_number = data?.patient_mobile_number ?? data?.mobile_number ?? data?.mobile_no ?? "-";

  const outsourceTooltip = (data?.outsource_lab_name || data?.outsourced_due_date) && (
    <Box>
      <Typography fontSize="0.85dvw" fontWeight="bold">Outsource Due Details</Typography>
      <Typography fontSize="0.85dvw">Outsource Lab : {data?.outsource_lab_name || "-"}</Typography>
      <Typography fontSize="0.85dvw">Due Date : {data?.outsourced_due_date || "-"}</Typography>
    </Box>
  );

  const avatarSrc = data?.patient_photo_url || data?.photo_url || (whatGender === "M" ? (ImagePaths.Male?.default ?? ImagePaths.Male) : (ImagePaths.Female?.default ?? ImagePaths.Female));

  return (
    <div className="eMed_Com_Patient_Details">
      <div>
        <img className="eMed_Com_Patient_AvaIcon" src={avatarSrc} alt="Avatar" />
      </div>
      <Box className="eMed_Pateint_Detail_LongData">
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography id="eMed_Com_Patient_Name" style={{ fontSize: "0.8dvw" }}>
            {(patient_name?.length > 20 ? `${patient_name.slice(0, 15)}...` : patient_name) + " " + (patient_age || "-") + "/" + (whatGender || "-")}
          </Typography>
          {data?.is_outsource_over_due && outsourceTooltip && (
            <Tooltip title={outsourceTooltip} placement="top" arrow>
              <span style={{ marginLeft: "0.5vw", fontSize: "0.75dvw", color: "#e65100", fontWeight: 600 }}>Overdue</span>
            </Tooltip>
          )}
        </Box>
        <Typography id="eMed_Com_Patient_UhidText">{PatientAccNumber + " | " + (mobile_number || "-")}</Typography>
      </Box>
    </div>
  );
}

export default function OutsourceTest() {
  const userAccess = CheckAccessFunc("Laboratory", "Home", "Outsource List", null, "Tab");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);
  const [totalTest, setTotalTest] = useState(0);
  const [totalSpecimenCollect, setTotalSpecimenCollect] = useState(0);
  const [totalReceived, setTotalReceived] = useState(0);
  const [totalTransmitted, setTotalTransmitted] = useState(0);
  const [totalResultUpload, setTotalResultUpload] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [testOutSourceLabGet, setTestOutSourceLabGet] = useState([]);
  const [testOutsourceList, setTestOutsourceList] = useState([]);
  const [selectOutLabName, setSelectOutLabName] = useState({});
  const [uploadedImages, setUploadedImages] = useState([]);
  const [selectedUploadData, setSelectedUploadData] = useState(null);
  const [filt_status, setFiltStatus] = useState("");
  const [testNameData, setTestNameData] = useState([]);
  const [PatientKey, setPatientKey] = useState("");
  const [testName, setTestName] = useState(null);
  const [labName, setLabName] = useState(null);
  const [statusKey, setStatusKey] = useState("All");
  const [fromDate, setFromDate] = useState(DateTime.fromJSDate(new Date()).toFormat("yyyy-MM-dd"));
  const [toDate, setToDate] = useState(DateTime.fromJSDate(new Date()).toFormat("yyyy-MM-dd"));
  const [dateIndex, setDateIndex] = useState(1);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [selectTransmitData, setSelectTransmitData] = useState([]);
  const [disableBtn, setDisableBtn] = useState(false);
  const [isLoader, setLoader] = useState(false);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState("");
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState("");
  const [confirmationPopup, setConfirmationPopup] = useState(false);
  const [selectedItem, setSelectedItem] = useState({});
  const [sourceOpen, setSourceOpen] = useState(false);
  const [uploadData, setUploadData] = useState(null);
  const MAX_RESULT_DOCUMENTS = 3;
  const [pdfPreview, setPdfPreview] = useState(false);
  const [pdfData, setPdfData] = useState("");
  const [ImgData, setImgData] = useState("");
  const [isTransfer, setIsTransfer] = useState(false);
  const [transList, setTransList] = useState({});
  const [contactName, setContactName] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [others, setOthers] = useState("");
  const [outsource_due_date, setOutsourceDueDate] = useState(null);
  const [contactList, setContactList] = useState([]);
  const [mobileNoList, setMobileNoList] = useState([]);
  const [imgCapClked, setImgCapClked] = useState(false);
  const [CapturedImg, setCapturedImg] = useState(null);
  const uploadImageRef = useRef(null);

  const closeWebCamPop = useCallback(() => {
    setImgCapClked(false);
    setCapturedImg(null);
  }, []);

  const errorMessage = useCallback((msg) => {
    setIsErrorMsg(true);
    setIsErrorMsgText(msg || "");
  }, []);
  const successMessage = useCallback((msg) => {
    setSuccessMsg(true);
    setSuccessMsgText(msg || "");
  }, []);
  const msgClose = useCallback(() => {
    setIsErrorMsg(false);
    setIsErrorMsgText("");
    setSuccessMsg(false);
    setSuccessMsgText("");
  }, []);

  const FilterDate = useCallback((from, to, index) => {
    setFromDate(from);
    setToDate(to);
    setDateIndex(index);
  }, []);

  const removeDuplicate = (data) => {
    const nameListJsonObject = data.map(JSON.stringify);
    const nameListUniqueSet = new Set(nameListJsonObject);
    return Array.from(nameListUniqueSet).map(JSON.parse);
  };

  /** Test name dropdown: same source as ServiceMaster – GET histopathology/service/config/ with module_type HISTOPATHOLOGY; flatten service_details and unique by service_name. */
  const getTestNameList = useCallback(() => {
    const params = new URLSearchParams({
      module_type: "HISTOPATHOLOGY",
      service_head_id: "",
      service_details_id: "",
      service_head: "",
      service_name: "",
      service_head_code: "",
      charge_type: "Normal",
    });
    RestAPIService.getAll(Serviceurls.NEW_DEPARTMENT_MASTER + "?" + params.toString())
      .then((res) => {
        if (res?.data?.status === "success") {
          const raw = res.data.data || [];
          const byHead = {};
          raw.forEach((row) => {
            const code = row.service_head_code ?? "";
            if (!byHead[code]) {
              byHead[code] = { head_name: row.service_head ?? "", service_head_code: code, service_details: [] };
            }
            const details = byHead[code].service_details;
            const has = details.some(
              (s) => (s.service_code ?? s.service_details_id) === (row.service_code ?? row.service_details_id)
            );
            if (!has) {
              details.push({
                service_code: row.service_code,
                service_name: row.service_name,
                service_details_id: row.service_details_id,
              });
            }
          });
          const serviceDetailsArray = Object.values(byHead).flatMap((item) =>
            (item?.service_details || []).map((s) => ({
              test_name: s.service_name,
              service_name: s.service_name,
              service_code: s.service_code,
              service_details_id: s.service_details_id,
            }))
          );
          setTestNameData(serviceDetailsArray?.length ? removeDuplicate(serviceDetailsArray) : []);
        }
      })
      .catch((err) => errorMessage(err?.response?.data?.message || err?.message));
  }, [errorMessage]);

  /** GET /histopathology/result-page/outsource/transit-detail/ — unique contact_person and mobile_no for transfer suggestions */
  const getLabTransitData = useCallback(() => {
    RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_RESULT_PAGE_OUTSOURCE_TRANSIT_DETAIL)
      .then((res) => {
        if (res?.data?.status === "success") {
          setMobileNoList(res.data.mobile_no || []);
          setContactList(res.data.contact_person || []);
        }
      })
      .catch((e) => errorMessage(e?.message));
  }, [errorMessage]);

  /** Outsource Laboratory dropdown: same GET as Outsource Configuration (GET /histopathology/outsource/config/). */
  const getOutSourceLabData = useCallback(() => {
    RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_OUTSOURCE_CONFIG)
      .then((res) => {
        if (res?.data?.status === "success") {
          setTestOutSourceLabGet(res.data.data || []);
        }
      })
      .catch((e) => errorMessage(e?.message));
  }, [errorMessage]);

  /** Map outsource-bills API row to table row shape (patient_details, specimen, lab, transit, etc.). */
  const mapOutsourceBillsRow = useCallback((row, index) => {
    const patient_name =
      row.patient_name ||
      row.name ||
      row.patient?.name ||
      row.patient_details?.patient_name ||
      `${(row.first_name || row.patient?.first_name || "").trim()} ${(row.last_name || row.patient?.last_name || "").trim()}`.trim() ||
      "";
    return {
      id: row.result_detail_id ?? row.bill_line_item_id ?? index + 1,
      sno: index + 1,
      bill_id: row.bill_id,
      bill_line_item_id: row.bill_line_item_id,
      bill_number: row.bill_number,
      nxt_id: row.nxt_id,
      billed_on: row.billed_on ?? row.billed_date ?? row.bill_date,
      collected_on: row.collected_on ?? row.collected_date ?? row.specimen_collected_date,
      patient_id: row.patient_id,
      patient_name,
      age: row.age,
      gender: row.gender,
      patient_identifier: row.patient_identifier,
      mobile_no: row.mobile_no,
      bill_amount: row.bill_amount,
      balance: row.balance,
      test_names: row.test_names ?? row.test_name,
      specimen: row.specimen,
      sample_id: row.sample_id,
      overall_sample_id: row.overall_sample_id,
      result_detail_id: row.result_detail_id,
      result_document: row.result_document,
      outsource_lab_id: row.outsource_lab_id ?? null,
      contact_person: row.outsource_contact_person ?? row.contact_person ?? null,
      contact_mobile_no: row.outsource_contact_mobile ?? null,
      comments: row.outsource_comments ?? row.comments ?? null,
      outsource_due_date: row.outsource_due_date,
      patient_details: {
        patient_name,
        patient_age_str: row.age,
        age: row.age,
        gender: row.gender,
        uhid: row.patient_identifier,
        patient_acc_no: row.patient_identifier,
        mobile_number: row.mobile_no,
        patient_mobile_number: row.mobile_no,
      },
      service_head: "-",
      service_name: (row.test_name ?? row.test_names) || "-",
      test_name: (row.test_name ?? row.test_names) || "-",
      status: row.status || "-",
    };
  }, []);

  const LabSourceList = useCallback(
    (status) => {
      setDisableBtn(true);
      setFiltStatus(status ?? "");
      setLoader(true);
      const outsourceStatus = (status ?? statusKey) === "All" ? "" : (status ?? statusKey);
      const queryParams = new URLSearchParams({
        from_date: fromDate || "",
        to_date: toDate || "",
        outsource_lab_id: labName?.id ?? "",
        test_id: testName?.service_details_id ?? testName?.test_name ?? "",
        patient_name_uhid: PatientKey || "",
        outsource_status: outsourceStatus,
      });
      const url = `${Serviceurls.HISTOPATHOLOGY_RESULT_PAGE_OUTSOURCE_BILLS}?${queryParams.toString()}`;

      RestAPIService.getAll(url)
        .then((res) => {
          if (res?.data?.status === "success") {
            setDisableBtn(false);
            setLoader(false);
            const data = res?.data?.data;
            const list = Array.isArray(data) ? data.map((row, i) => mapOutsourceBillsRow(row, i)) : [];
            const cardDetails = res?.data?.total_by_status || {};
            setTestOutsourceList(list);
            setTotalTest(cardDetails.total_tests || 0);
            setTotalSpecimenCollect(cardDetails.specimen_collected || 0);
            setTotalTransmitted(cardDetails.transferred || 0);
            setTotalReceived(cardDetails.received || 0);
            setTotalResultUpload(cardDetails.result_uploaded || 0);
            setRowCount(list.length);
            setFiltStatus(status ?? "");
          } else {
            setDisableBtn(false);
            setLoader(false);
            errorMessage(res?.data?.message || "Failed to load list");
          }
        })
        .catch((err) => {
          setDisableBtn(false);
          setLoader(false);
          errorMessage(err?.response?.data?.message || err?.message);
        });
    },
    [fromDate, toDate, labName, testName, PatientKey, statusKey, mapOutsourceBillsRow, errorMessage]
  );

  const setDefaultLabSelection = useCallback((data) => {
    const updated = {};
    data?.forEach((row) => {
      if (row?.laboratories?.length === 1 && row.status === "Specimen Collected") {
        updated[row.id] = row.laboratories[0];
      }
    });
    if (Object.keys(updated).length > 0) {
      setSelectOutLabName((prev) => ({ ...prev, ...updated }));
    }
  }, []);

  useEffect(() => {
    getTestNameList();
    getOutSourceLabData();
    getLabTransitData();
    LabSourceList("");
  }, []);

  useEffect(() => {
    if (testOutsourceList?.length > 0) setDefaultLabSelection(testOutsourceList);
  }, [testOutsourceList?.length]);

  const getOutSourceListPrint = () => {
    setDisableBtn(true);
    const statusFilter = statusKey && statusKey !== "All" ? statusKey : filt_status ?? "";
    const queryParams = new URLSearchParams({
      status: statusFilter,
      patient_name: PatientKey || "",
      from_date: fromDate,
      to_date: toDate,
      test_name: testName?.test_name || "",
      lab_id: labName?.id || "",
      export: "pdf",
    }).toString();
    RestAPIService.excelGet(`${Serviceurls.HISTOPATHOLOGY_OUTSOURCE}?${queryParams}`)
      .then((res) => {
        const file = new Blob([res.data], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL);
        setDisableBtn(false);
      })
      .catch((err) => {
        errorMessage(err?.response?.data?.message || err?.message);
        setDisableBtn(false);
      });
  };

  const handleTableCheckbox = (selectedIds) => {
    const list = testOutsourceList || [];
    const specimenCollectedRows = list.filter((r) => r.status === "Specimen Collected");
    const isSelectAll = selectedIds.length === specimenCollectedRows.length;

    if (isSelectAll) {
      const invalidRows = list.filter(
        (row) => row.status === "Specimen Collected" && (!selectOutLabName?.[row.id] || !selectOutLabName[row.id]?.id)
      );
      if (invalidRows.length > 0) {
        errorMessage("Kindly Select Outsource Lab");
        setSelectedRowIds([]);
        return;
      }
    }

    const selectedRows = list.filter((row) => selectedIds.includes(row.sno));
    const invalidRows = selectedRows.filter((row) => !selectOutLabName?.[row.id] || !selectOutLabName[row.id]?.id);
    if (invalidRows.length > 0) {
      errorMessage("Kindly Select Outsource Lab");
      const validSelections = selectedIds.filter((id) => !invalidRows.some((row) => row.sno === id));
      setSelectedRowIds(validSelections);
      return;
    }
    const finalData = selectedRows.map((row) => ({
      id: row.id || null,
      status: row.status || null,
      outsource_lab_id: selectOutLabName?.[row.id]?.id ?? null,
    }));
    setSelectTransmitData(finalData);
    setSelectedRowIds(selectedIds);
  };

  const SendOutsourceLab = () => {
    const datas = {
      data: [],
      action: "Transferred",
      contact_person: contactName || "",
      mobile_no: mobileNo || "",
      comments: others || "",
      outsource_due_date: outsource_due_date ? DateTime.fromJSDate(new Date(outsource_due_date)).toFormat("yyyy-MM-dd") : null,
    };

    if (Object.keys(transList).length > 0 && transList?.id) {
      const selectedLabID = selectOutLabName?.[transList.id];
      datas.data = [{ id: transList.id, status: transList.status, outsource_lab_id: selectedLabID?.id }];
    } else {
      datas.data = [...(selectTransmitData || [])];
    }

    if (mobileNo.length !== 10) {
      errorMessage("Mobile Number must be 10 digits.");
      return;
    }
    if (!CommonValidation.numberOnly(mobileNo)) {
      errorMessage("Please enter only digits in the mobile number.");
      return;
    }
    if (!datas?.data?.length) {
      errorMessage("Kindly Select Any Data");
      return;
    }

    setDisableBtn(true);
    // PUT /histopathology/result-page/outsource-list/ — action: Transferred | Received | Result Uploaded; data: [{ id, outsource_lab_id, status }]; optional: contact_person, mobile_no, comments, outsource_due_date
    RestAPIService.updateWithOutId(datas, Serviceurls.HISTOPATHOLOGY_RESULT_PAGE_OUTSOURCE_LIST)
      .then((res) => {
        if (res.data.status === "success") {
          LabSourceList(filt_status);
          successMessage(res.data.message);
          getLabTransitData();
          setSelectTransmitData([]);
          setSelectOutLabName({});
          setSelectedRowIds([]);
          setTransList({});
          setIsTransfer(false);
          setContactName("");
          setMobileNo("");
          setOthers("");
          setOutsourceDueDate(null);
          setDisableBtn(false);
        }
      })
      .catch((err) => {
        errorMessage(err?.response?.data?.status === "fail" ? err.response.data.message : err?.message);
        setDisableBtn(false);
      });
  };

  const sendReceiveStatus = () => {
    const datas = { data: [], action: "Received" };
    if (selectedItem?.id) {
      datas.data.push({
        id: selectedItem.id,
        status: selectedItem.status,
        outsource_lab_id: selectedItem.outsource_lab_id,
      });
    }
    if (!datas.data.length) return;
    setDisableBtn(true);
    RestAPIService.updateWithOutId(datas, Serviceurls.HISTOPATHOLOGY_RESULT_PAGE_OUTSOURCE_LIST)
      .then((res) => {
        if (res.data.status === "success") {
          LabSourceList(filt_status);
          successMessage(res.data.message);
          setDisableBtn(false);
          setConfirmationPopup(false);
          setSelectedItem({});
        }
      })
      .catch((err) => {
        errorMessage(err?.response?.data?.message || err?.message);
        setDisableBtn(false);
      });
  };

  const getS3Key = async (doc_key, id) => {
    try {
      const res = await RestAPIService.getAllTwo(Serviceurls.AWS_URL_GET + "?doc_key=" + encodeURIComponent(doc_key));
      if (res.data.status === "success") {
        return {
          data_url: res.data.data,
          id,
          type: doc_key?.split(".")?.pop() === "pdf" ? "pdf" : "image",
        };
      }
    } catch (e) {
      errorMessage("Error fetching S3 URL");
    }
    return null;
  };

  const openSourceModal = async (list) => {
    const image =
      typeof list?.result_document === "string" && list.result_document?.trim() !== ""
        ? JSON.parse(list.result_document)
        : list?.result_document ?? [];
    const tempArr = [];
    if (image?.length) {
      for (let j = 0; j < image.length; j++) {
        const doc_key = image[j]?.document_file;
        const id = list.id;
        const getData = await getS3Key(doc_key, id);
        if (getData) tempArr.push({
          ...getData,
          savedBefore: true,
          document_file: image[j]?.document_file,
          document_name: image[j]?.document_name || "document",
        });
      }
    }
    setSelectedUploadData(list);
    setUploadedImages(tempArr);
    setUploadData(list);
    setSourceOpen(true);
  };

  const sourceClose = () => {
    setSourceOpen(false);
    setSelectedUploadData(null);
    setUploadData(null);
    LabSourceList(filt_status);
  };

  const onImageRemove = (index) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const onChangeFile = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const maxNew = MAX_RESULT_DOCUMENTS - uploadedImages.length;
    if (maxNew <= 0) {
      errorMessage(`Maximum ${MAX_RESULT_DOCUMENTS} documents allowed.`);
      e.target.value = "";
      return;
    }
    const toAdd = files.slice(0, maxNew);
    if (files.length > maxNew) errorMessage(`Only ${maxNew} more document(s) allowed (max ${MAX_RESULT_DOCUMENTS} total).`);
    setUploadedImages((prev) => [...prev, ...toAdd]);
    e.target.value = "";
  };

  const uploadOutsourceImage = (list) => {
    const resultDetailId = list?.result_detail_id ?? list?.id;
    if (!resultDetailId) {
      errorMessage("Missing result detail");
      return;
    }

    const existingDocs = uploadedImages
      .filter((img) => img.savedBefore && img.document_file)
      .map((img) => ({ document_file: img.document_file, document_name: img.document_name || "document" }));
    const newFiles = uploadedImages.filter((img) => img instanceof File);

    const totalCount = existingDocs.length + newFiles.length;
    if (totalCount === 0) {
      errorMessage("Please add at least one document");
      return;
    }
    if (totalCount > MAX_RESULT_DOCUMENTS) {
      errorMessage(`Maximum ${MAX_RESULT_DOCUMENTS} documents allowed (existing + new).`);
      return;
    }

    const dataForm = new FormData();
    dataForm.append("result_detail_id", resultDetailId);
    newFiles.forEach((file) => dataForm.append("result_images", file));
    dataForm.append("result_document", JSON.stringify(existingDocs));

    setDisableBtn(true);
    RestAPIService.postFormData(dataForm, Serviceurls.HISTOPATHOLOGY_RESULT_PAGE_OUTSOURCE_RESULT_UPLOAD)
      .then((res) => {
        if (res?.data?.status === "success") {
          setUploadedImages([]);
          setSelectedUploadData(null);
          setUploadData(null);
          setDisableBtn(false);
          successMessage(res?.data?.message || "Saved");
          LabSourceList(filt_status);
          sourceClose();
        } else {
          setDisableBtn(false);
          errorMessage(res?.data?.message || "Upload failed");
        }
      })
      .catch((err) => {
        errorMessage(err?.response?.data?.message || "Image upload failed");
        setDisableBtn(false);
      });
  };

  const deleteUploadImages = (index, list) => {
    const row = list || selectedUploadData || uploadData;
    const item = uploadedImages[index];
    if (!row || !item?.savedBefore) {
      onImageRemove(index);
      return;
    }
    const resultImageArray =
      typeof row?.result_document === "string" && row.result_document?.trim() !== ""
        ? JSON.parse(row.result_document)
        : Array.isArray(row?.result_document) ? row.result_document : [];
    if (!resultImageArray?.length) {
      errorMessage("No documents to delete");
      return;
    }
    resultImageArray.splice(index, 1);
    const updatedResultImage = resultImageArray.length > 0 ? JSON.stringify(resultImageArray) : "";
    const data = {
      result_id: row?.result_item_id ?? row?.result_detail_id ?? row?.id,
      result_document: updatedResultImage,
    };
    RestAPIService.delete(Serviceurls.HISTOPATHOLOGY_REPORT_DATA, data)
      .then((res) => {
        if (res?.data?.status === "success") {
          onImageRemove(index);
          successMessage(res?.data?.message || "Document removed");
        } else {
          errorMessage(res?.data?.message || "Delete failed");
        }
      })
      .catch((err) => errorMessage(err?.response?.data?.message || "Delete failed"));
  };

  const fetchAndConvertToBase64 = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader?.result);
        reader.onerror = () => reject(reader?.error);
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error("Error fetching or converting file:", err);
    }
  };

  const ClosePopUp = () => {
    setPdfPreview(false);
    setPdfData("");
    setImgData("");
  };

  const addImgCapture = (data) => {
    if (data) {
      setUploadedImages((prev) => {
        if (prev.length >= MAX_RESULT_DOCUMENTS) {
          errorMessage(`Maximum ${MAX_RESULT_DOCUMENTS} documents allowed.`);
          return prev;
        }
        return [...prev, dataURItoFile(data, `result_document_${Date.now()}.jpg`)];
      });
      setImgCapClked(false);
    }
  };

  const listWithSno = (testOutsourceList || []).map((el, index) => {
    const fromFirstLast = `${el?.patient_details?.first_name || ""} ${el?.patient_details?.last_name || ""}`.trim();
    const patient_name = fromFirstLast || el?.patient_name || "";
    return {
      ...el,
      sno: index + 1,
      patient_name,
      patient_details: { ...(el.patient_details || {}), patient_name },
    };
  });

  const gridToolBar = () => (
    <Box>
      <CommonGridToolBarWithFilterText
        searchHolder="Search"
        FilterTextArray={[
          { label: "From Date", value: formatDate(fromDate) },
          { label: "To Date", value: formatDate(toDate) },
        ]}
      />
    </Box>
  );

  const columns = [
    {
      field: "patient_name",
      flex: 0.12,
      headerName: "Patient Details",
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Box sx={{ height: "100%", width: "100%", display: "flex", alignItems: "center", mt: "0.7vw" }}>
          <OutsourcePatientDetails
            data={{
              ...params?.row?.patient_details,
              nxt_id: params?.row?.nxt_id,
              is_outsource_over_due: params?.row?.is_outsource_over_due,
              outsource_lab_name: params?.row?.outsource_lab_name,
              outsourced_due_date: params?.row?.outsourced_due_date,
            }}
          />
        </Box>
      ),
    },
    // {
    //   field: "group_name",
    //   flex: 0.08,
    //   headerName: "Service Head",
    //   headerAlign: "center",
    //   align: "center",
    //   renderCell: (params) => (
    //     <Box>
    //       {params.row?.service_head?.length > 14 ? (
    //         <Tooltip title={params.row?.service_head} placement="top" arrow>
    //           <Typography style={{ fontSize: "1dvw" }}>{params.row?.service_head?.slice(0, 14) + "..."}</Typography>
    //         </Tooltip>
    //       ) : (
    //         <Typography style={{ marginLeft: "0.2vw" }}>{params.row?.service_head || "-"}</Typography>
    //       )}
    //     </Box>
    //   ),
    // },
    {
      field: "test_name",
      flex: 0.15,
      headerName: "Service Details",
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ height: "100%", width: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          {params.row?.test_name?.length > 35 ? (
            <Tooltip title={params.row?.test_name} placement="top" arrow>
              <Typography style={{ fontSize: "0.8dvw" }}>{params.row?.test_name?.slice(0, 35) + "..."}</Typography>
            </Tooltip>
          ) : (
            <Typography style={{ fontSize: "0.8dvw" }}>{params.row?.test_name || "-"}</Typography>
          )}
          <Typography id="eMed_OP_AptTbl_AptText1">
            {`Billed On: ${params?.row?.billed_on ? formatDate(params.row.billed_on) : "-"}`}
          </Typography>
        </Box>
      ),
    },
    {
      field: "specimen",
      flex: 0.12,
      headerName: "Specimen Details",
      headerAlign: "center",
      renderCell: (params) => {
        const collected_date = `Collected On: ${params?.row?.collected_on ? formatDate(params.row.collected_on) : "-"}`;
        return (
          <Box sx={{ height: "100%", width: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Typography id="eMed_OP_AptTbl_AptText1" style={{ display: "flex", alignItems: "center" }} fontWeight="bold">
              Specimen :{" "}
              {params.row?.specimen?.length > 20 ? (
                <Tooltip title={params.row?.specimen} placement="top" arrow>
                  <Typography style={{ marginLeft: "0.2vw", fontSize: "0.8dvw" }}>{params.row?.specimen?.slice(0, 17) + "..."}</Typography>
                </Tooltip>
              ) : (
                <Typography style={{ marginLeft: "0.2vw", fontSize: "0.8dvw" }}>{params.row?.specimen || "-"}</Typography>
              )}
            </Typography>
            {collected_date?.length > 34 ? (
              <Tooltip title={collected_date} placement="top" arrow>
                <Typography id="eMed_OP_AptTbl_AptText1">{collected_date?.slice(0, 33) + "..."}</Typography>
              </Tooltip>
            ) : (
              <Typography id="eMed_OP_AptTbl_AptText1">{collected_date}</Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "laboratories",
      flex: 0.15,
      headerName: "Outsource Laboratory",
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const rowIndex = params.row.id;
        return (
          <Box sx={{ height: "100%", width: "100%", display: "flex", alignItems: "center" }}>
            <Autocomplete
              size="small"
              sx={{ width: "20vw", mr: "0.5vw" }}
              disabled={!!params.row?.outsource_lab_name || params.row?.status !== "Specimen Collected"}
              options={testOutSourceLabGet}
              getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.outsource_laboratory_name) || ""}
              value={
                selectOutLabName?.[params.row.id] ??
                testOutSourceLabGet?.find((lab) => lab.id === params.row.outsource_lab_id && params.row.outsource_lab_id) ??
                null
              }
              onChange={(e, value) => {
                setSelectOutLabName((prev) => ({ ...prev, [rowIndex]: value || null }));
                setSelectTransmitData((prev) =>
                  prev?.map((d) => (d.id === rowIndex ? { ...d, outsource_lab_id: value?.id ?? null } : d)) ?? []
                );
              }}
              onKeyDown={(e) => e.stopPropagation()}
              renderInput={(p) => <TextField {...p} label="Select Laboratory" />}
            />
          </Box>
        );
      },
    },
    {
      field: "contact_person",
      flex: 0.1,
      headerName: "Transit Details",
      renderCell: (params) => (
        <Box>
          {params?.row?.contact_person || params?.row?.contact_mobile_no ? (
            <Box>
              <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                {params?.row?.contact_person?.length > 12 ? (
                  <Tooltip title={params?.row?.contact_person} placement="top">
                    <Typography sx={{ fontWeight: "bold", fontSize: "0.8dvw" }}>{params?.row?.contact_person?.slice(0, 12) + "..."}</Typography>
                  </Tooltip>
                ) : (
                  <Typography fontWeight="bold" sx={{ fontSize: "0.8dvw" }}>{params?.row?.contact_person || "-"}</Typography>
                )}
                {params?.row?.comments ? (
                  <Tooltip title={params?.row?.comments} placement="top">
                    <IconButton size="small">
                      <img src={ImagePaths.InfoFill?.default ?? ImagePaths.InfoFill} style={{ width: ".9vw", height: ".9vw" }} alt="info" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Box>
              <Typography sx={{ color: Colors.themeDark, fontSize: "0.8dvw" }}>
                {params?.row?.contact_mobile_no || params?.row?.mobile_no || "-"}
              </Typography>
            </Box>
          ) : (
            "-"
          )}
        </Box>
      ),
    },
    {
      field: "status",
      flex: 0.15,
      headerName: "Status",
      type: "number",
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const status = params.row?.status;
        const statusClass =
          status === "Specimen Collected"
            ? "eMed_Lab_Status_specimen"
            : status === "Transferred"
              ? "eMed_Lab_Status_transfer"
              : status === "Received"
                ? "eMed_Lab_Status_Received"
                : status === "Result Uploaded"
                  ? "eMed_Lab_Status_ResultUpload"
                  : "";
        const imgSrc =
          status === "Specimen Collected"
            ? ImagePaths.LabSpecimen?.default ?? ImagePaths.LabSpecimen
            : status === "Transferred"
              ? ImagePaths.LabTransferred?.default ?? ImagePaths.LabTransferred
              : status === "Received"
                ? ImagePaths.LabReceived?.default ?? ImagePaths.LabReceived
                : status === "Result Uploaded"
                  ? ImagePaths.GreenTick?.default ?? ImagePaths.GreenTick
                  : null;
        return (
          <Box component="div" style={{ width: "15vw" }}>
            <Box component="div" className={statusClass}>
              {imgSrc && <img src={imgSrc} style={{ padding: "9px" }} alt="" />}
              <Typography>{status}</Typography>
            </Box>
          </Box>
        );
      },
    },
  ];

  if (userAccess?.editAccess) {
    columns.push({
      field: "action",
      flex: 0.07,
      headerName: "Action",
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.25vw",
          }}
        >
          {params.row?.status === "Specimen Collected" ? (
            <Tooltip title="Transfer" placement="top" arrow>
              <IconButton
                size="small"
                emed_tid="out_Transfer"
                onClick={() => {
                  const selectedLabID = selectOutLabName?.[params?.row?.id];
                  if (!selectedLabID?.id) {
                    errorMessage("Kindly Select Outsource Lab");
                    return;
                  }
                  setIsTransfer(true);
                  setTransList(params.row);
                }}
                sx={{ p: "0.25vw", minWidth: 0 }}
              >
                <Box
                  component="img"
                  src={
                    params.row?.status === "Specimen Collected"
                      ? (ImagePaths.LabTransfer?.default ?? ImagePaths.LabTransfer)
                      : (ImagePaths.LabTransferDbsl?.default ?? ImagePaths.LabTransferDbsl)
                  }
                  alt="Transfer"
                  className="eMed_action_img"
                />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Receive" placement="top" arrow>
              <IconButton
                size="small"
                disabled={!(params.row?.status === "Transferred" && !disableBtn)}
                emed_tid="out_Receive"
                className="eMed_usrconf_btn"
                onClick={() => {
                  setConfirmationPopup(true);
                  setSelectedItem(params.row);
                }}
                sx={{ p: "0.25vw", minWidth: 0 }}
              >
                <Box
                  component="img"
                  src={
                    params.row?.status === "Transferred"
                      ? (ImagePaths.LabReceive?.default ?? ImagePaths.LabReceive)
                      : (ImagePaths.LabReceiveDbsl?.default ?? ImagePaths.LabReceiveDbsl)
                  }
                  alt="Receive"
                  className="eMed_action_img"
                />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Result Upload" placement="top" arrow>
            <IconButton
              size="small"
              emed_tid="out_upload"
              className="eMed_usrconf_btn"
              disabled={params.row.status !== "Received" && params.row.status !== "Result Uploaded"}
              onClick={() => {
                setUploadData(params.row);
                openSourceModal(params.row);
              }}
              sx={{ p: "0.25vw", minWidth: 0 }}
            >
              <Box
                component="img"
                src={
                  params.row?.status === "Received" || params.row?.status === "Result Uploaded"
                    ? (ImagePaths.LabUpload?.default ?? ImagePaths.LabUpload)
                    : (ImagePaths.DisableUpload?.default ?? ImagePaths.DisableUpload)
                }
                alt="Upload"
                className="eMed_action_img"
              />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    });
  }

  const renderFilterScreen = () => (
    <Box component="div" className="eMed_rts_filter_container">
      <Box component="div" className="eMed_filter_Top" sx={{ borderBottom: "1px solid", borderColor: "divider", pb: 1.5 }}>
        <Typography variant="h6" fontWeight={600}>Filter</Typography>
        <Button emed_tid="outfilt_close" onClick={() => setFilterOpen(false)} className="eMed_usrconf_btn">
          <img src={ImagePaths.LabCloseButton?.default ?? ImagePaths.LabCloseButton} alt="close" className="eMed_action_img_small" />
        </Button>
      </Box>
      <Box className="eMed_rts_middle" sx={{ marginTop: 2 }}>
        <Box className="eMed_date_filter">
          <CommonDatePicker
            FromDate={fromDate}
            ToDate={toDate}
            DateIndex={dateIndex}
            SelectedDates={FilterDate}
            HideAllMenu={true}
            height={true}
          />
        </Box>
        <Box className="eMed_rts_dropdown">
          <Autocomplete
            options={testOutSourceLabGet}
            getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.outsource_laboratory_name) ?? ""}
            onChange={(e, value) => setLabName(value)}
            value={labName ?? null}
            renderInput={(params) => <TextField {...params} label="Outsource Laboratory Name" size="small" />}
            fullWidth
          />
        </Box>
        <Box className="eMed_rts_dropdown">
          <Autocomplete
            size="small"
            options={testNameData?.length ? testNameData : []}
            getOptionLabel={(opt) => opt?.test_name ?? ""}
            value={testName ?? null}
            onChange={(e, newValue) => setTestName(newValue)}
            renderInput={(params) => <TextField {...params} label="Select Test Name" />}
            fullWidth
          />
        </Box>
        <Box className="eMed_rts_dropdown">
          <TextField
            label="Patient Name / UHID"
            size="small"
            autoComplete="off"
            value={PatientKey}
            onChange={(e) => setPatientKey(e.target.value)}
            fullWidth
          />
        </Box>
      </Box>
      <Stack spacing={2} direction="row" className="eMed_rts_btn" alignItems="center" justifyContent="center">
        <Button
          variant="outlined"
          size="small"
          emed_tid="fil_clear"
          onClick={() => {
            setFromDate(DateTime.fromJSDate(new Date()).toFormat("yyyy-MM-dd"));
            setToDate(DateTime.fromJSDate(new Date()).toFormat("yyyy-MM-dd"));
            setDateIndex(1);
            setPatientKey("");
            setTestName(null);
            setLabName(null);
            setStatusKey("All");
            LabSourceList("");
          }}
          id="eMed_Config_btn"
        >
          Clear
        </Button>
        <Button
          variant="contained"
          size="small"
          emed_tid="fil_search"
          disabled={disableBtn}
          onClick={() => {
            LabSourceList();
            setFilterOpen(false);
          }}
          id="eMed_Config_btn"
        >
          Search
        </Button>
      </Stack>
    </Box>
  );

  const transferPopup = () => (
    <Modal open>
      <Paper elevation={3} id="emedhub_Transit_mainBox">
        <div id="emedhub_vendorDetails_header">
          <Typography>Transit Details</Typography>
          <IconButton
            size="small"
            onClick={() => {
              setContactName("");
              setMobileNo("");
              setOthers("");
              setIsTransfer(false);
            }}
          >
            <img className="emedhub_transit_img" src={ImagePaths.LabCloseButton?.default ?? ImagePaths.LabCloseButton} alt="close" />
          </IconButton>
        </div>
        <Box sx={{ marginTop: "1vw", display: "flex", justifyContent: "center" }}>
          <Autocomplete
            clearIcon
            size="small"
            freeSolo
            sx={{ width: "20vw" }}
            options={contactList?.length ? contactList : []}
            getOptionLabel={(o) => o}
            value={contactName || ""}
            onInputChange={(e, newInputValue) => {
              if (newInputValue?.length <= 32) {
                if (CommonValidation.alphabetOnly(newInputValue)) setContactName(newInputValue);
                else if (newInputValue.length > 0) errorMessage("Only alphabets allowed");
              }
            }}
            onChange={(e, newValue) => {
              if (typeof newValue === "string" && CommonValidation.alphabetOnly(newValue)) setContactName(newValue);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Contact Person Name" size="small" inputProps={{ ...params.inputProps, maxLength: 32 }} />
            )}
          />
        </Box>
        <Box sx={{ marginTop: "1vw", display: "flex", justifyContent: "center" }}>
          <Autocomplete
            clearIcon
            size="small"
            freeSolo
            sx={{ width: "20vw" }}
            options={mobileNoList?.length ? mobileNoList : []}
            getOptionLabel={(o) => o}
            value={mobileNo || ""}
            onInputChange={(e, newInputValue) => {
              if (CommonValidation.numberOnly(newInputValue)) setMobileNo(newInputValue);
              else if (newInputValue?.length > 0) errorMessage("Only Numbers allowed");
            }}
            onChange={(e, newValue) => {
              if (CommonValidation.numberOnly(newValue)) setMobileNo(newValue);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Mobile No" size="small" inputProps={{ ...params.inputProps, maxLength: 10 }} />
            )}
          />
        </Box>
        <Box sx={{ marginTop: "1vw", display: "flex", justifyContent: "center" }}>
          <TextField
            size="small"
            label="Others"
            value={others}
            name="others"
            onChange={(e) => setOthers(e.target.value)}
            sx={{ width: "20vw" }}
            variant="outlined"
            inputProps={{ maxLength: 250 }}
          />
        </Box>
        <Box sx={{ marginTop: "1vw", display: "flex", justifyContent: "center" }}>
          <TextField
            type="date"
            size="small"
            label="Outsource Due Date"
            value={outsource_due_date ? (typeof outsource_due_date === "string" ? outsource_due_date : DateTime.fromJSDate(new Date(outsource_due_date)).toFormat("yyyy-MM-dd")) : ""}
            onChange={(e) => setOutsourceDueDate(e.target.value || null)}
            sx={{ width: "20vw" }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: DateTime.fromJSDate(new Date()).toFormat("yyyy-MM-dd") }}
          />
        </Box>
        <Box sx={{ marginTop: "3vw" }}>
          <Stack direction="row" spacing={2} sx={{ width: "100%", justifyContent: "flex-end" }}>
            <Button size="small" sx={{ width: "5vw", textTransform: "capitalize" }} variant="outlined" onClick={() => { setContactName(""); setMobileNo(""); setOthers(""); }}>
              Clear
            </Button>
            <Button
              size="small"
              sx={{ width: "5vw", textTransform: "capitalize" }}
              variant="contained"
              disabled={disableBtn}
              onClick={() => {
                if (!contactName || !mobileNo) errorMessage("Please enter Contact Name and Mobile No");
                else SendOutsourceLab();
              }}
            >
              Submit
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Modal>
  );

  const renderSourcePopUp = () => (
    <Modal open={sourceOpen} onClose={sourceClose} aria-labelledby="modal-title" aria-describedby="modal-description">
      <Box className="emedhub_popup_outsource_list">
        <div className="emedhub_popup_title">
          <label id="modal-title" className="emedhub_popup_title_left">Test Report</label>
          <div className="emedhub_card_close_button">
            <img className="emedhub_popup_title_right" src={ImagePaths.LabCloseButton?.default ?? ImagePaths.LabCloseButton} onClick={sourceClose} alt="close" />
          </div>
        </div>
        <div className="emedhub_popup_title_2" />
        <div className="emedhub_print_popup">
          <input type="file" multiple accept="application/pdf,image/*" onChange={onChangeFile} ref={uploadImageRef} style={{ display: "none" }} />
          <div className="image_top_header">
            <label className="test_name_style">
              <b>Service Name:</b>
              <span style={{ marginLeft: "5px" }}>{"  " + (uploadData?.service_name || "-")}</span>
            </label>
            <div className="emedhub_image_upload_button">
              <Button size="small" variant="contained" sx={{ height: "2vw", textTransform: "capitalize" }} onClick={() => uploadImageRef.current?.click()}>
                Upload PDF
              </Button>
              <Button size="small" onClick={() => setImgCapClked(true)}>
                <img className="eMed_imgCap_btn" src={ImagePaths.captureImg?.default ?? ImagePaths.captureImg} alt="camera" />
              </Button>
            </div>
          </div>
          <div style={{ display: "flex", width: "100%", overflow: "auto", overflowY: "hidden" }}>
            {uploadedImages?.map((item, index) => (
              <div key={index} className="emedhub_multiple_image_size_outlist">
                <img
                  alt="Preview"
                  src={
                    item.type === "image" || (item instanceof File && item.type?.startsWith("image"))
                      ? (item.data_url || URL.createObjectURL(item))
                      : (item.type === "pdf" || (item instanceof File && item.type?.includes("pdf")))
                        ? (ImagePaths.DocumentPreview?.default ?? ImagePaths.DocumentPreview)
                        : (ImagePaths.DocumentPreview?.default ?? ImagePaths.DocumentPreview)
                  }
                  className="emedhub_multiple_image_resolution"
                  onClick={() => {
                    if (item.type === "pdf" && item?.savedBefore) {
                      setImgData("");
                      fetchAndConvertToBase64(item.data_url).then((base64) => {
                        if (base64) {
                          const b = base64.replace("data:binary/octet-stream;", "data:application/pdf;");
                          setPdfPreview(true);
                          setPdfData(b);
                        }
                      });
                    } else if (item.type === "application/pdf" || (item instanceof File && item.type?.includes("pdf"))) {
                      setImgData("");
                      setPdfPreview(true);
                      setPdfData(URL.createObjectURL(item));
                    } else if (item?.type === "image" || (item instanceof File && item.type?.startsWith("image"))) {
                      setPdfData("");
                      setPdfPreview(true);
                      setImgData(item.data_url || URL.createObjectURL(item));
                    }
                  }}
                />
                <IconButton
                  size="small"
                  className="emedhub_icon_style_outlist"
                  onClick={() => deleteUploadImages(index, selectedUploadData || uploadData)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
          </div>
        </div>
        <div className="emedhub_popup_button">
          <Button variant="outlined" onClick={() => setUploadedImages([])} sx={{ marginRight: "1vw", textTransform: "capitalize" }}>
            Reset
          </Button>
          <Button
            disabled={disableBtn || !uploadedImages?.length}
            variant="contained"
            onClick={() => {
              setDisableBtn(true);
              uploadOutsourceImage(selectedUploadData || uploadData);
            }}
            sx={{ textTransform: "capitalize" }}
          >
            Save
          </Button>
        </div>
      </Box>
    </Modal>
  );

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
      <Box component="div" className="eMed_Pharma_rts_header" sx={{ flexShrink: 0 }}>
        <Box component="div" flex={0.8} display="flex">
          <Box style={{ cursor: "pointer" }} onClick={() => LabSourceList("")}>
            {AmountsCard("Total Test", totalTest, true, filt_status === "" ? "#04b7b1" : "white", false, null)}
          </Box>
          <Box style={{ cursor: "pointer" }} onClick={() => LabSourceList("Specimen Collected")}>
            {AmountsCard("Specimen Collected", totalSpecimenCollect, true, filt_status === "Specimen Collected" ? "#04b7b1" : "white", false, null)}
          </Box>
          <Box style={{ cursor: "pointer" }} onClick={() => LabSourceList("Transferred")}>
            {AmountsCard("Transferred", totalTransmitted, true, filt_status === "Transferred" ? "#04b7b1" : "white", false, null)}
          </Box>
          <Box style={{ cursor: "pointer" }} onClick={() => LabSourceList("Received")}>
            {AmountsCard("Received", totalReceived, true, filt_status === "Received" ? "#04b7b1" : "white", false, null)}
          </Box>
          <Box style={{ cursor: "pointer" }} onClick={() => LabSourceList("Result Uploaded")}>
            {AmountsCard("Result Uploaded", totalResultUpload, true, filt_status === "Result Uploaded" ? "#04b7b1" : "white", false, null)}
          </Box>
        </Box>
        <Box flex={0.2} display="flex" justifyContent="flex-end" alignItems="center" marginRight="0.5vw">
          <Button
            variant="contained"
            id="eMed_Btn_Text"
            sx={{ width: "13vw" }}
            disabled={disableBtn || !userAccess?.editAccess}
            onClick={() => {
              if (selectTransmitData?.length > 0) {
                const invalid = selectTransmitData.filter((row) => !row.outsource_lab_id);
                if (invalid.length > 0) {
                  errorMessage("Kindly Select Outsource Lab");
                  return;
                }
                setIsTransfer(true);
              }
            }}
          >
            TRANSFER TEST
          </Button>
          <Tooltip title="Filter" placement="top" arrow>
            <Button className="eMed_usrconf_btn" onClick={() => setFilterOpen(true)}>
              <img src={ImagePaths.Filter?.default ?? ImagePaths.Filter} alt="filter" className="eMed_action_img" style={{ height: "2.3vw", width: "2.3vw" }} />
            </Button>
          </Tooltip>
          {/* <Tooltip title="Print" placement="top" arrow>
            <Button emed_tid="outpay_print" className="eMed_usrconf_btn" disabled={disableBtn} onClick={getOutSourceListPrint}>
              <img src={ImagePaths.LabPrintIcon?.default ?? ImagePaths.LabPrintIcon} alt="print" className="eMed_action_img" style={{ height: "2.3vw", width: "2.3vw" }} />
            </Button>
          </Tooltip> */}
        </Box>
      </Box>

      <Box component="div" className="eMed_Pharma_rts_tbl_card" sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "65dvh", overflow: "hidden" }}>
        <DataGrid
          className="eMed_Pharma_table"
          sx={{
            flex: 1,
            minHeight: 0,
            "& .MuiDataGrid-columnHeaders": { alignItems: "center" },
            "& .MuiDataGrid-columnHeader": {
              display: "flex",
              alignItems: "center",
              paddingTop: "12px",
              paddingBottom: "12px",
            },
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              paddingTop: "12px",
              paddingBottom: "12px",
            },
            "& .MuiDataGrid-cell > div": {
              height: "100%",
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
            },
            "& .MuiDataGrid-footerContainer": {
              overflow: "hidden",
              minHeight: "2vw",
              alignItems: "center",
            },
          }}
          isRowSelectable={(params) => !!(userAccess?.editAccess && params.row.status === "Specimen Collected")}
          checkboxSelection={!!userAccess?.editAccess}
          onRowSelectionModelChange={(model) => handleTableCheckbox(Array.from(model.ids))}
          rows={listWithSno}
          rowSelectionModel={{ type: "include", ids: new Set(selectedRowIds ?? []) }}
          columns={columns}
          getRowId={(row) => row.sno}
          paginationModel={paginationModel}
          onPaginationModelChange={(m) => {
            setPaginationModel(m);
            setPage(m.page);
            setPageSize(m.pageSize);
          }}
          loading={isLoader}
          hideFooterSelectedRowCount
          localeText={{ toolbarColumns: "", toolbarDensity: "", toolbarExport: "", toolbarFilters: "", toolbarExportPrint: "" }}
          slots={{ toolbar: gridToolBar, noRowsOverlay: () => <Stack className="eMed_conf_nodata">No Records Found</Stack> }}
          slotProps={{ noRowsOverlay: {} }}
          headerHeight={40}
          rowHeight={64}
          pageSizeOptions={[10, 20, 30]}
          disableRowSelectionOnClick
          pagination
        />
      </Box>

      <Drawer anchor="right" open={filterOpen} onClose={() => setFilterOpen(false)} PaperProps={{ sx: { width: "35vw", minWidth: 320 } }}>
        {renderFilterScreen()}
      </Drawer>

      {confirmationPopup && (
        <ConfirmPopup
          cnfTitle="Confirmation"
          cnfContent1="Are you sure, Do you want to receive ?"
          cnfAlertPopupClose={() => { setConfirmationPopup(false); setSelectedItem({}); }}
          cnfData={sendReceiveStatus}
        />
      )}
      {isErrorMsg && <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
      {sourceOpen && renderSourcePopUp()}
      {pdfPreview && (
        <Modal open onClose={ClosePopUp}>
          <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "60vw", height: "80vh", bgcolor: "white", boxShadow: 24, p: 1 }}>
            <Box display="flex" justifyContent="flex-end">
              <IconButton onClick={ClosePopUp}><CloseIcon /></IconButton>
            </Box>
            {ImgData ? <img src={ImgData} alt="Preview" style={{ maxWidth: "100%", maxHeight: "75vh" }} /> : <iframe title="PDF" src={pdfData} style={{ width: "100%", height: "75vh" }} />}
          </Box>
        </Modal>
      )}
      {imgCapClked && (
        <OpenWebCamPopUP
          capturedImg={CapturedImg}
          closePopup={closeWebCamPop}
          addImgCapture={addImgCapture}
        />
      )}
      {isTransfer && transferPopup()}
    </div>
  );
}
